import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useApi from '../../hooks/useApi';
import Draggable from 'react-draggable';
import ThemeDropdownWithPreview from '../themesDropdown';
import posthog from 'posthog-js';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import useFixedViewportWidth from '../useFixedViewPortWidth';
import { getPageThemeChrome } from '../../reader/pageThemeChrome';
import useReaderAffiliatePromo from '../../hooks/useReaderAffiliatePromo';

/** Spread resume index (same as last_read_snapshot) → physical snapshot index range for the snapshots API. */
function computeSnapshotWindowForResume(resume, totalSnap) {
  let start = 0;
  let end = 19;
  if (totalSnap > 0) {
    end = Math.min(19, totalSnap - 1);
  }
  if (resume !== 0 && totalSnap > 0) {
    const wStart = resume * 2 - 10;
    const wEnd = resume * 2 + 9;
    if (wStart < 0) {
      start = 0;
      end = Math.min(19, totalSnap - 1);
    } else if (wEnd < totalSnap && wEnd !== totalSnap) {
      start = wStart;
      end = wEnd;
    } else {
      start = wStart;
      end = wStart + (totalSnap - wStart) - 1;
    }
  }
  return { start, end };
}

const SNAPSHOT_PAGE_SIZE = 650;
const LOUPE_WIDTH = Math.round(1.5 * SNAPSHOT_PAGE_SIZE);
const LOUPE_HEIGHT = Math.round(0.5 * SNAPSHOT_PAGE_SIZE);
const LOUPE_ZOOM = 1.5;

const ENHANCED_PAGE_BASE = 650;
const READER_FONT_SCALE_MIN = 0.85;
const READER_FONT_SCALE_MAX = 1.5;
const READER_FONT_BASE_PX = 16;
const READER_FONT_STORAGE_KEY = 'poppyruz_reader_font_scale';
const READING_LAMP_STORAGE_KEY = 'poppyruz_reading_lamp_on';
/** "Dark Background" — no image URL; toggled via moon icon, hidden from settings dropdown. */
const PITCH_BLACK_THEME_ID = 'THEME0000014';

function BasicTheme({ book_id }) {
  const { recordForwardFlip, promoElement } = useReaderAffiliatePromo('pdf_reader');
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [targetPage, setTargetPage] = useState(0);
  const { request, loading, error } = useApi();
  const [totalPages, setTotalPages] = useState(0);
  const [pageSlotStart, setPageSlotStart] = useState(0);
  const [pageSlotEnd, setPageSlotEnd] = useState(0);
  const [SnapshotTotalPages, setSnapshotTotalPages] = useState(0);
  const pageMap = useMemo(() => {
    return Object.fromEntries(pages.map(p => [p.page_order, p]));
  }, [pages]);
  //const getPage = (index) => (index >= 0 && index < pages.length ? pages[index] : null);
  const getPage = (order) => pageMap[order] || null;
  const leftPage = getPage(currentPage * 2+1);
  const rightPage = getPage(currentPage * 2 + 1+1);
  const previousLeftPage = getPage((currentPage - 1) * 2+1);
  const nextRightPage = getPage((currentPage + 1) * 2 + 1+1);
  const [initialLoading, setInitialLoading] = useState(true);
  const textRefs = useRef([]); 
  const [textHeights, setTextHeights] = useState({});
  const [zoomedImage, setZoomedImage] = useState(null);
  const [editingPages, setEditingPages] = useState({});
  const [draftTexts, setDraftTexts] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [pageStatus, setPageStatus] = useState({}); 
  const [viewMode, setViewMode] = useState('enhanced'); // 'enhanced' or 'basic'
  const [snapshotsOnly, setSnapshotsOnly] = useState(false);
  const [snapshotSizingMode, setSnapshotSizingMode] = useState('fit'); // 'fit' | 'original' — basic view only
  const [readerFontScale, setReaderFontScale] = useState(() => {
    try {
      const raw = localStorage.getItem(READER_FONT_STORAGE_KEY);
      if (raw == null || raw === '') return 1;
      const v = parseFloat(raw);
      if (Number.isNaN(v)) return 1;
      return Math.min(
        READER_FONT_SCALE_MAX,
        Math.max(READER_FONT_SCALE_MIN, v),
      );
    } catch {
      return 1;
    }
  });
  const [loupeActive, setLoupeActive] = useState(false);
  const [loupePointer, setLoupePointer] = useState({ x: 0, y: 0 });
  const [readingLampOn, setReadingLampOn] = useState(() => {
    try {
      const raw = localStorage.getItem(READING_LAMP_STORAGE_KEY);
      return raw === '1' || raw === 'true';
    } catch {
      return false;
    }
  });
  /** Last non–pitch-black background (for listbox + restoring when exiting pitch black). */
  const [pitchBlackRestoreTheme, setPitchBlackRestoreTheme] = useState(null);
  const snapshotImgRefs = useRef({});
  const [snapshotsByIndex, setSnapshotsByIndex] = useState({});
  const [snapshotSlotStart, setSnapshotSlotStart] = useState(0);
  const [snapshotSlotEnd, setSnapshotSlotEnd] = useState(0);
  const [fade, setFade] = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [loadedSnapshots, setLoadedSnapshots] = useState({});
  const [lastPageRead, setLastPageRead] = useState(0);
  const [lastReadSnapshot, setLastReadSnapshot] = useState(0);
  const [jumpLoading, setJumpLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [audioVolume, setAudioVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const [backgroundThemes, setBackgroundThemes] = useState([]);
  const [pageThemes, setPageThemes] = useState([]);
  const getBackgroundTheme = (index) => (index >= 0 && index < backgroundThemes.length ? backgroundThemes[index] : null);
  const getPageTheme = (index) => (index >= 0 && index < pageThemes.length ? pageThemes[index] : null);
  const [selectedPageTheme, setSelectedPageTheme] = useState(getPageTheme(0));
  const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState(getBackgroundTheme(0));
  const [previewTheme, setPreviewTheme] = useState({});
  const [loadingTheme, setLoadingTheme] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [canEdit, setcanEdit] = useState(true);
  const inFlight = useRef(new Set());
  let audio = null;

  const clampedReaderFontScale = Math.min(
    READER_FONT_SCALE_MAX,
    Math.max(READER_FONT_SCALE_MIN, readerFontScale),
  );
  const enhancedScaledPagePx = Math.round(ENHANCED_PAGE_BASE * clampedReaderFontScale);
  const fixedPanelPx =
    viewMode === 'enhanced' ? enhancedScaledPagePx : ENHANCED_PAGE_BASE;
  const viewportLockWidth =
    viewMode === 'enhanced' ? enhancedScaledPagePx * 2 + 10 : 1310;
  useFixedViewportWidth(viewportLockWidth);

  const fetchInitialPages = useCallback(async () => {
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start: 0, end: 19 },
      });

      console.log('Initial API Response:', data);

      if (!data?.book) {
        return;
      }

      const b = data.book;
      setTotalPages(parseInt(b.poppyruz_pages_count, 10));
      setSnapshotTotalPages(parseInt(b.original_pages_count, 10));
      const snapTotal = parseInt(b.original_pages_count, 10);

      if (b.is_public_domain === 1) {
        setcanEdit(false);
      }

      const snapshotsOnlyBook =
        parseInt(String(b.is_snapshots_only), 10) === 1 ||
        b.is_snapshots_only === true;

      if (snapshotsOnlyBook) {
        setSnapshotsOnly(true);
        setViewMode('basic');
        setPages([]);
        setCurrentPage(0);
        setTargetPage(0);
        setLastReadSnapshot(0);
        setLoadedSnapshots({});
        const { start, end } = computeSnapshotWindowForResume(0, snapTotal);
        const snapData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
          params: { start, end },
        });
        const batch = mergeSnapshotsBatch(start, snapData.snapshots || []);
        setSnapshotsByIndex(batch);
        setSnapshotSlotStart(start);
        setSnapshotSlotEnd(end);
        return;
      }

      setSnapshotsOnly(false);
      setPages(data.pages || []);
      setPageSlotStart(0);
      setPageSlotEnd(19);
    } catch (err) {
      console.error('Initial API Error:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [request, book_id]);

  const fetchNextPages = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
    .every(num => pages.some(p => p.page_number === num));
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start, end },
      });

      console.log(`Fetched pages ${start}–${end}:`, data?.pages?.map(p => p.page_number));

      if (data) {
        setPages(prevPages => prevPages.concat(data.pages));
        console.log(data.book.is_public_domain);
        if(data.book.is_public_domain === 1){
          setcanEdit(false);
        }
      }
    } catch (err) {
      console.error('API Error:', err);
    }finally {
      setInitialLoading(false);
    }
  };

  const fetchPreviousPages = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
    .every(num => pages.some(p => p.page_number === num));
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/books/${book_id}/`,
        params: { start, end },
      });
      if (data) {
        setPages(prevPages => data.pages.concat(prevPages));
      }
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const updateUserBookDetail = async ({ theme_id, last_read_page, last_read_snapshot } = {}) => {
    const payload = {};
    if (theme_id !== undefined) payload.theme_id = theme_id;
    if (last_read_page !== undefined) payload.last_read_page = last_read_page;
    if (last_read_snapshot !== undefined) payload.last_read_snapshot = last_read_snapshot;
    if (Object.keys(payload).length === 0) return;
    try {
      await request({
        method: 'patch',
        url: `/api/v1/pdf_parser/my_book_view/${book_id}/`,
        withAuth: true,
        data: payload,
      });
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const mergeSnapshotsBatch = (start, list) => {
    const batch = {};
    (list || []).forEach((snap, i) => {
      batch[start + i] = snap;
    });
    return batch;
  };

  const fetchSnapshots = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
      .every((num) => snapshotsByIndex[num] != null && snapshotsByIndex[num]?.snap_shot_url);
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
        params: { start, end },
      });
      if (data?.snapshots?.length) {
        setSnapshotsByIndex((prev) => ({ ...prev, ...mergeSnapshotsBatch(start, data.snapshots) }));
      }
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const fetchPreviousSnapshots = async (start, end) => {
    const alreadyFetched = Array.from({ length: end - start + 1 }, (_, i) => start + i)
      .every((num) => snapshotsByIndex[num] != null && snapshotsByIndex[num]?.snap_shot_url);
    if (alreadyFetched) return;
    try {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
        params: { start, end },
      });
      if (data?.snapshots?.length) {
        const batch = mergeSnapshotsBatch(start, data.snapshots);
        setSnapshotsByIndex((prev) => ({ ...batch, ...prev }));
      }
    } catch (err) {
      console.error('API Error:', err);
    }
  };

  const markSnapshotLoaded = useCallback((pageIndex) => {
    setLoadedSnapshots((prev) => {
      if (prev[pageIndex]) return prev;
      return { ...prev, [pageIndex]: true };
    });
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        let user_id = 'guest';
        if (token !== null) {
          user_id = localStorage.getItem('user_id');
        }
        const themeData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/themes/${user_id}/`,
        });
        if (!themeData) throw new Error("No theme data");
        const backgroundThemes = themeData.book_themes || [];
        const pageThemes = themeData.page_themes || [];
        setBackgroundThemes(backgroundThemes);
        setPageThemes(pageThemes);
        setSelectedBackgroundTheme(themeData.book_themes[0]);
        console.log('themes are loaded');
        if (token !== null) {
          const userData = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/my_book_view/${book_id}/`,
            withAuth: true
          });
          if (userData?.book_detail) {
            const bd = userData.book_detail;
            const snapshotsOnlyBook =
              parseInt(String(bd.is_snapshots_only), 10) === 1 ||
              bd.is_snapshots_only === true;
            setSnapshotsOnly(snapshotsOnlyBook);

            setTotalPages(parseInt(bd.poppyruz_pages_count));
            setSnapshotTotalPages(parseInt(bd.original_pages_count));
            const snapTotal = parseInt(bd.original_pages_count, 10);

            const rawLrs = bd.last_read_snapshot;
            const parsedLrs =
              rawLrs != null && rawLrs !== '' ? parseInt(rawLrs, 10) : 0;
            const safeLrs = Number.isNaN(parsedLrs) ? 0 : parsedLrs;
            setLastReadSnapshot(safeLrs);

            if (snapshotsOnlyBook) {
              setCurrentPage(safeLrs);
              setTargetPage(safeLrs);
              setViewMode('basic');
              const { start, end } = computeSnapshotWindowForResume(safeLrs, snapTotal);
              try {
                const snapData = await request({
                  method: 'get',
                  url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
                  params: { start, end },
                });
                setLoadedSnapshots({});
                const batch = mergeSnapshotsBatch(start, snapData.snapshots || []);
                setSnapshotsByIndex(batch);
                setSnapshotSlotStart(start);
                setSnapshotSlotEnd(end);
              } catch (snapErr) {
                console.error('Snapshots-only initial load error:', snapErr);
              } finally {
                setInitialLoading(false);
              }
            } else {
              setCurrentPage(bd.last_read_page);
              setTargetPage(bd.last_read_page);
              if (safeLrs !== 0 && snapTotal > 0) {
                const { start: s0, end: e0 } = computeSnapshotWindowForResume(safeLrs, snapTotal);
                try {
                  const snapData = await request({
                    method: 'get',
                    url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
                    params: { start: s0, end: e0 },
                  });
                  const batch = mergeSnapshotsBatch(s0, snapData.snapshots || []);
                  setSnapshotsByIndex(batch);
                  setSnapshotSlotStart(s0);
                  setSnapshotSlotEnd(e0);
                } catch (snapErr) {
                  console.error('Prefetch snapshots error:', snapErr);
                }
              }
            }

            const matchedTheme = backgroundThemes.find(t => t.theme_meta_unique_id === bd.theme_id);
            if (matchedTheme) {
              handleBackgroundThemeChange(matchedTheme);
            } else {
              console.warn('No matching theme found for theme_id:', bd.theme_id);
            }

            if (!snapshotsOnlyBook) {
              if (bd.last_read_page !== 0) {
                const start = (bd.last_read_page * 2) - 10;
                const end = (bd.last_read_page * 2) + 9;
                if (start < 0) {
                  fetchInitialPages();
                } else {
                  if (end < parseInt(bd.poppyruz_pages_count, 10) && end != parseInt(bd.poppyruz_pages_count, 10)) {
                    fetchNextPages(start, end);
                    setPageSlotStart(start);
                    setPageSlotEnd(end);
                  } else {
                    const newEnd = start + (parseInt(bd.poppyruz_pages_count, 10) - start) - 1;
                    fetchNextPages(start, newEnd);
                    setPageSlotStart(start);
                    setPageSlotEnd(newEnd);
                  }
                }
              } else {
                fetchInitialPages();
              }
            }
          }
        } else {
          console.log('Initial load (guest)');
          fetchInitialPages();
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        fetchInitialPages();
      } 
    };
    loadInitialData();
  }, []);  

  const nextPage = () => {
    if (viewMode === 'basic') {
      if (
        snapshotSlotEnd + 1 < SnapshotTotalPages ||
        snapshotSlotEnd + 1 === SnapshotTotalPages
      ) {
        if (currentPage + 1 < Math.ceil(SnapshotTotalPages / 2)) {
          setTargetPage(currentPage + 1);
          recordForwardFlip();
          if (currentPage == Math.ceil((snapshotSlotEnd + 1) / 2) - 3) {
            const start = snapshotSlotEnd + 1;
            const end = start + 19;
            if (start < SnapshotTotalPages) {
              if (end < SnapshotTotalPages && end !== SnapshotTotalPages) {
                fetchSnapshots(start, end);
                setSnapshotSlotEnd(end);
              } else {
                const newEnd = start + (SnapshotTotalPages - start) - 1;
                fetchSnapshots(start, newEnd);
                setSnapshotSlotEnd(newEnd);
              }
            }
          }
        }
      }
      return;
    }
    if((pageSlotEnd+1)<totalPages || (pageSlotEnd+1)===totalPages){
      if (currentPage+1<Math.ceil(totalPages / 2)){
        console.log(pageSlotEnd)
        setTargetPage(currentPage + 1);
        recordForwardFlip();
        if (currentPage == Math.ceil((pageSlotEnd+1)/2)-3){
          console.log(currentPage)
          const start = pageSlotEnd+1;
          const end = start + 19;
          console.log('total pages');
          console.log(totalPages);
          if(start < totalPages){
            if(end < totalPages && end != totalPages){
              fetchNextPages(start, end);
              setPageSlotEnd(end);
            }else{
              const newEnd = start+((totalPages - start)-1);
              fetchNextPages(start, newEnd);
              setPageSlotEnd(newEnd);
            }
          }
        }
      }
    }
  };

  const previousPage = () => {
    if (targetPage <= 0) return;
    if (viewMode === 'basic') {
      if (((snapshotSlotStart + 1) / 2) < currentPage - 1) {
        if (currentPage - 1 == Math.ceil((snapshotSlotStart + 1) / 2 + 3)) {
          const end = snapshotSlotStart - 1;
          const start = end - 19;
          if (start > 0) {
            fetchPreviousSnapshots(start, end);
            setSnapshotSlotStart(start);
          } else {
            fetchPreviousSnapshots(0, end);
            setSnapshotSlotStart(0);
          }
          setTargetPage(currentPage - 1);
        } else {
          setTargetPage(currentPage - 1);
        }
      } else {
        const end = snapshotSlotStart - 1;
        const start = end - 19;
        if (start > 0) {
          fetchPreviousSnapshots(start, end);
          setSnapshotSlotStart(start);
        } else {
          fetchPreviousSnapshots(0, end);
          setSnapshotSlotStart(0);
        }
        setTargetPage(currentPage - 1);
      }
      return;
    }
    if (((pageSlotStart + 1) / 2) < currentPage - 1) {
      if (currentPage - 1 == Math.ceil((pageSlotStart + 1) / 2 + 3)) {
        const end = pageSlotStart - 1;
        const start = end - 19;
        if (start > 0) {
          fetchPreviousPages(start, end);
          setPageSlotStart(start);
        } else {
          fetchPreviousPages(0, end);
          setPageSlotStart(0);
        }
        setTargetPage(currentPage - 1);
      } else {
        setTargetPage(currentPage - 1);
      }
    } else {
      const end = pageSlotStart - 1;
      const start = end - 19;
      if (start > 0) {
        fetchPreviousPages(start, end);
        setPageSlotStart(start);
      } else {
        fetchPreviousPages(0, end);
        setPageSlotStart(0);
      }
      setTargetPage(currentPage - 1);
    }
  };

  const physicalTotalPages =
    viewMode === 'basic' ? SnapshotTotalPages : totalPages;
  const displayedPhysicalPage = currentPage * 2 + 1;
  const jumpPageOptions = useMemo(() => {
    if (!physicalTotalPages || physicalTotalPages <= 0) return [];
    return Array.from({ length: physicalTotalPages }, (_, i) => i + 1);
  }, [physicalTotalPages]);

  const jumpToPhysicalPage = async (rawPage) => {
    const totalPhysical = physicalTotalPages;
    if (!totalPhysical || totalPhysical <= 0 || jumpLoading) return;

    const parsed = parseInt(String(rawPage), 10);
    if (Number.isNaN(parsed)) return;

    const page = Math.max(1, Math.min(totalPhysical, parsed));
    const spread = Math.floor((page - 1) / 2);
    const maxSpread = Math.ceil(totalPhysical / 2) - 1;
    const safeSpread = Math.max(0, Math.min(maxSpread, spread));
    if (safeSpread === targetPage && safeSpread === currentPage) return;

    const { start, end } = computeSnapshotWindowForResume(safeSpread, totalPhysical);

    setJumpLoading(true);
    try {
      if (viewMode === 'basic') {
        await fetchSnapshots(start, end);
        setSnapshotSlotStart(start);
        setSnapshotSlotEnd(end);
      } else {
        await fetchNextPages(start, end);
        setPageSlotStart(start);
        setPageSlotEnd(end);
      }
      setTargetPage(safeSpread);
      posthog.capture('jump_to_page', {
        book_id,
        view_mode: viewMode,
        physical_page: page,
        spread: safeSpread,
      });
    } finally {
      setJumpLoading(false);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
        audioRef.current.volume = audioVolume;
        audioRef.current.muted = isMuted;
        if(selectedBackgroundTheme){
          if (selectedBackgroundTheme?.has_audio && selectedBackgroundTheme.audio_url) {
            audioRef.current.src = selectedBackgroundTheme.audio_url;
            audioRef.current.play().catch(() => {});
          } else {
            audioRef.current.pause();
          }
        }
    }
  }, [selectedBackgroundTheme, audioVolume, isMuted]);

  useEffect(() => {
    if (pages.length > 0) {
      const heights = {};
      textRefs.current.forEach((ref, idx) => {
        if (ref) {
          heights[idx] = ref.offsetHeight;
        }
      });
      setTextHeights(heights);
    }
  }, [pages, readerFontScale, viewMode]);

  useEffect(() => {
    try {
      localStorage.setItem(READER_FONT_STORAGE_KEY, String(readerFontScale));
    } catch {
      /* ignore quota / private mode */
    }
  }, [readerFontScale]);

  useEffect(() => {
    try {
      localStorage.setItem(READING_LAMP_STORAGE_KEY, readingLampOn ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, [readingLampOn]);

  useEffect(() => {
    if (selectedBackgroundTheme?.theme_meta_unique_id !== PITCH_BLACK_THEME_ID) {
      setPitchBlackRestoreTheme(selectedBackgroundTheme);
    }
  }, [selectedBackgroundTheme]);

  const deletePage = async (pageId) => {
    setPageStatus(prev => ({ ...prev, [pageId]: 'deleting' }));
    try {
      const pageToDelete = pages.find(p => p.page_meta_unique_id === pageId);
      if (!pageToDelete) throw new Error("Page not found");
      const deletedOrder = pageToDelete.page_order;
      const response = await request({
        method: 'delete',
        url: `/api/v1/pdf_parser/pages/${pageId}/`,
      });
      if (response.success === true) {
          const updatedPages = pages
          .filter(p => p.page_meta_unique_id !== pageId)
          .map(p => {
            if (p.page_order > deletedOrder) {
              return { ...p, page_order: p.page_order - 1 };
            }
            return p;
          });
        setPages(updatedPages);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Error deleting page.');
    } finally {
      setPageStatus(prev => ({ ...prev, [pageId]: 'idle' }));
    }

    setEditingPages(prev => {
      const updated = { ...prev };
      delete updated[pageId];
      return updated;
    });
  };

  const switchToBasicView = async () => {
    if (snapshotsOnly) return;
    setFade(false);
    setLoadingSnapshots(true);
    setTimeout(async () => {
      try {
        setLastPageRead(currentPage);
        const resume = lastReadSnapshot;
        const totalSnap = SnapshotTotalPages;
        const { start, end } = computeSnapshotWindowForResume(resume, totalSnap);

        const snapshotData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/snapshots/${book_id}/`,
          params: { start, end },
        });

        setLoadedSnapshots({});
        const batch = mergeSnapshotsBatch(start, snapshotData.snapshots || []);
        setSnapshotsByIndex(batch);
        setSnapshotSlotStart(start);
        setSnapshotSlotEnd(end);
        setTargetPage(resume);
        setCurrentPage(resume);
        setSelectedPageTheme(getPageTheme(0));
        setViewMode('basic');
        setFade(true);
      } catch (err) {
        console.error('Failed to load snapshots', err);
        alert('Failed to switch to basic view!');
        setFade(true);
      } finally {
        setLoadingSnapshots(false);
      }
    },300);
    posthog.capture('switch_to_basic_view', {
      book_id,
      last_page_read: currentPage,
      last_read_snapshot: lastReadSnapshot,
    });
  };

  const backToEnhancedView = () => {
    if (snapshotsOnly) return;
    setFade(false);
    setTargetPage(lastPageRead);
    setTimeout(() => {
      setViewMode('enhanced');
      setSnapshotSizingMode('fit');
      setLoupeActive(false);
      setSnapshotsByIndex({});
      setLoadedSnapshots({});
      setSnapshotSlotStart(0);
      setSnapshotSlotEnd(0);
      setFade(true);
    }, 300);
    posthog.capture('switch_to_enhanced_view', { book_id, return_page: lastPageRead });
  };

  const handlePageThemeChange = (theme) => {
    setLoadingTheme(theme); 
    const img = new Image();
    img.src = theme.theme_url;
    img.onload = () => {
      setSelectedPageTheme(theme); 
      setLoadingTheme(null);            
    };
    img.onerror = () => {
      alert("Failed to load theme image");
      setLoadingTheme(null);
    };
    posthog.capture('change_page_theme', {
      book_id,
      theme_name: theme.theme_name,
      theme_id: theme.theme_meta_unique_id
    });
  };

  const handleBackgroundThemeChange = (theme) => {
    if (!theme) return;
    setLoadingTheme(theme);
    if (theme.has_audio === 1 && theme.audio_url) {
      audio = new Audio(theme.audio_url);
    }
    const noImage =
      !theme.theme_url ||
      String(theme.theme_url).trim() === '' ||
      theme.theme_meta_unique_id === PITCH_BLACK_THEME_ID;
    if (noImage) {
      setSelectedBackgroundTheme(theme);
      setLoadingTheme(null);
      updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
      posthog.capture('change_background_theme', {
        book_id,
        theme_name: theme.theme_name,
        theme_id: theme.theme_meta_unique_id,
        has_audio: theme.has_audio === 1,
      });
      return;
    }
    const img = new Image();
    img.src = theme.theme_url;
    img.onload = () => {
      setSelectedBackgroundTheme(theme);
      setLoadingTheme(null);
    };
    img.onerror = () => {
      alert('Failed to load theme image');
      setLoadingTheme(null);
    };
    updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
    posthog.capture('change_background_theme', {
      book_id,
      theme_name: theme.theme_name,
      theme_id: theme.theme_meta_unique_id,
      has_audio: theme.has_audio === 1,
    });
  };

  const togglePitchBlackBackground = () => {
    const dark = backgroundThemes.find(
      (t) => t.theme_meta_unique_id === PITCH_BLACK_THEME_ID,
    );
    if (!dark) return;
    const goingDark =
      selectedBackgroundTheme?.theme_meta_unique_id !== PITCH_BLACK_THEME_ID;
    posthog.capture('pitch_black_background_toggle', {
      book_id,
      on: goingDark,
    });
    if (goingDark) {
      handleBackgroundThemeChange(dark);
      return;
    }
    const restore =
      pitchBlackRestoreTheme &&
      pitchBlackRestoreTheme.theme_meta_unique_id !== PITCH_BLACK_THEME_ID
        ? pitchBlackRestoreTheme
        : backgroundThemes.find(
            (t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID,
          );
    if (restore) handleBackgroundThemeChange(restore);
  };

  const parseVisualContent = (value) => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {
      try {
        const fixed = value.replace(/'/g, '"');
        const parsed = JSON.parse(fixed);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        
      }
    }
    const matches = value.match(/https?:\/\/[^\s'"]+/g);
    return matches || [];
  };

  const renderEndOfBookPage = () => (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 px-8 text-center select-none"
      aria-label="The end"
    >
      <svg
        viewBox="0 0 64 64"
        className="h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 14v36c0 1.5 1.2 2 2.5 1.5L32 44l17.5 7.5c1.3.5 2.5 0 2.5-1.5V14c0-1.5-1.2-2.5-2.5-2.5H14.5C13.2 11.5 12 12.5 12 14z"
        />
        <path strokeLinecap="round" d="M32 12.5V44" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M22 28h8M22 34h6" />
      </svg>
      <div className="h-px w-16 bg-gray-300" aria-hidden />
      <p className="font-serif text-2xl tracking-[0.2em] text-gray-500 uppercase">
        The End
      </p>
      <div className="h-px w-16 bg-gray-300" aria-hidden />
    </div>
  );

  const isPastEndOfBook = (pageIndex) =>
    physicalTotalPages > 0 && pageIndex >= physicalTotalPages;

  const renderPage = (page, pageIndex) => {
    if (isPastEndOfBook(pageIndex)) {
      return renderEndOfBookPage();
    }

    if (viewMode === 'basic') {
      const snapshotPage = snapshotsByIndex[pageIndex];
      if (!snapshotPage || !snapshotPage.snap_shot_url) {
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            Snapshot not available
          </div>
        );
      }
      const fitSnapshots = snapshotSizingMode === 'fit';
      return (
        <div
          className={
            fitSnapshots
              ? 'absolute inset-0 flex items-center justify-center'
              : 'relative inline-flex items-center justify-center w-auto h-auto min-w-0'
          }
        >
          {!loadedSnapshots[pageIndex] && (
            <div
              className={
                fitSnapshots
                  ? 'absolute inset-0 flex items-center justify-center bg-white z-10'
                  : 'absolute inset-0 flex min-h-[12rem] min-w-[12rem] items-center justify-center bg-white/90 z-10'
              }
            >
              <div className="w-6 h-6 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            key={`snap-${pageIndex}-${snapshotPage.snap_shot_url}`}
            src={snapshotPage.snap_shot_url}
            alt="Snapshot page"
            className={
              fitSnapshots ? 'w-[650px] h-[650px]' : 'block h-auto w-auto max-w-none'
            }
            style={fitSnapshots ? { objectFit: 'fill' } : undefined}
            loading="eager"
            decoding="async"
            onClick={() => {
              if (!loupeActive) setZoomedImage(snapshotPage.snap_shot_url);
            }}
            ref={(el) => {
              if (viewMode === 'basic') {
                if (el) snapshotImgRefs.current[pageIndex] = el;
                else delete snapshotImgRefs.current[pageIndex];
              }
              if (el?.complete && el.naturalHeight > 0) {
                markSnapshotLoaded(pageIndex);
              }
            }}
            onLoad={() => markSnapshotLoaded(pageIndex)}
            onError={() => markSnapshotLoaded(pageIndex)}
          />
        </div>
      );
    }

    if (!page) return null;
    
    const pageId = page.page_meta_unique_id;
    const visuals = parseVisualContent(page.visual_content);
    const hasText = page.page_content?.trim().length > 0;
    const hasVisuals = visuals.length > 0;

    const pageHeight = enhancedScaledPagePx;
    const safeMargin = 100;
    const textHeight = textHeights[pageId] || 0;
    const remainingHeight = pageHeight - textHeight - safeMargin;
    const visualMaxHeight = remainingHeight > 0 ? remainingHeight * 0.75 : 0;
    const isEditing = canEdit && editingPages[pageId];

    const startEditing = () => {
      setEditingPages(prev => ({ ...prev, [pageId]: true }));
      setDraftTexts(prev => ({ ...prev, [pageId]: page.page_content || '' }));
      setTimeout(() => {
        textRefs.current[pageId]?.focus();
      }, 0);
    };

    const saveEditing = async () => {
      const newText = draftTexts[pageId] || '';
      setPageStatus(prev => ({ ...prev, [pageId]: 'saving' }));
      try {
        const response = await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/pages/${pageId}/`,
          data: { page_content: newText },
        });
        if (response.success === 'true' || response.page_content) {
          setPages(prevPages =>
            prevPages.map(p => p.page_meta_unique_id === pageId
              ? { ...p, page_content: newText }
              : p
            )
          );
        }
      } catch (error) {
        console.error('Save error:', error);
        alert('Error saving page.');
      } finally {
        setPageStatus(prev => ({ ...prev, [pageId]: 'idle' }));
      }
      setEditingPages(prev => {
        const updated = { ...prev };
        delete updated[pageId];
        return updated;
      });
      setDraftTexts(prev => {
        const updated = { ...prev };
        delete updated[pageId];
        return updated;
      });
    };

    const cancelEditing = () => {
      setEditingPages(prev => {
        const updated = { ...prev };
        delete updated[pageId];
        return updated;
      });
      setDraftTexts(prev => {
        const updated = { ...prev };
        delete updated[pageId];
        return updated;
      });
    };

    return (
      <div className="flex flex-col h-full gap-4 overflow-hidden relative">
        {(pageStatus[pageId] === 'saving' || pageStatus[pageId] === 'deleting') && (
          <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="animate-spin h-10 w-10 border-4 border-violet-800 border-t-transparent rounded-full mx-auto mb-2"></div>
              <div className="text-sm font-semibold text-gray-600">
                {pageStatus[pageId] === 'saving' ? 'Saving...' : 'Deleting...'}
              </div>
            </div>
          </div>
        )}

        {hasText && isEditing && canEdit && (
          <Draggable bounds="parent">
            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 shadow rounded p-2 flex gap-2 z-20">
              <button onClick={saveEditing} className="px-2 py-1 bg-violet-800 text-white rounded text-sm">Save</button>
              <button onClick={cancelEditing} className="px-2 py-1 bg-red-500 text-white rounded text-sm">Cancel</button>
              <button
                onClick={() => {
                  setPageToDelete(pageId);
                  setShowDeleteModal(true);
                }}
                className="px-2 py-1 bg-red-600 text-white rounded text-sm"
              >
                Delete
              </button>
            </div>
          </Draggable>
        )}

        {hasText && (
          <div className="relative text-left overflow-hidden">
            <div
              ref={(el) => {
                textRefs.current[pageId] = el;
                if (el && isEditing && draftTexts[pageId] !== el.innerText) {
                  el.innerText = draftTexts[pageId] || '';
                }
              }}
              contentEditable={canEdit && isEditing}
              suppressContentEditableWarning
              onInput={(e) => {
                const content = e?.currentTarget?.innerText;
                if (typeof content === 'string') {
                  setDraftTexts((prev) => ({ ...prev, [pageId]: content }));
                }
              }}
              onClick={() => { if (!isEditing && canEdit) startEditing(); }}
              className={`p-2 whitespace-pre-line break-words text-justify ${isEditing ? 'border border-violet-400' : ''}`}
              style={{ minHeight: '100px' }}
            >
              {!isEditing && (page.page_content || '')}
            </div>
          </div>
        )}

        {hasVisuals && visualMaxHeight > 50 && (
          <div className="flex flex-col justify-center items-center gap-4 overflow-hidden" style={{ maxHeight: visualMaxHeight }}>
            {visuals.map((src, idx) => (
              <img
                key={idx}
                src={src}
                alt={`Visual ${idx + 1}`}
                className="object-contain w-auto max-w-full max-h-full rounded shadow cursor-pointer"
                onClick={() => {
                  setZoomedImage(src);
                  posthog.capture('zoom_visual', { book_id, page_index: page.page_order, image_url: src });
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
  
  useEffect(() => {
    if (currentPage !== targetPage) {
      const timer = setTimeout(() => {
        setCurrentPage(targetPage);
        if (viewMode === 'enhanced') {
          updateUserBookDetail({ last_read_page: targetPage });
        } else {
          updateUserBookDetail({ last_read_snapshot: targetPage });
          setLastReadSnapshot(targetPage);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [targetPage, currentPage, viewMode]);

  useEffect(() => {
    if (viewMode !== 'basic' || snapshotSizingMode !== 'fit') {
      setLoupeActive(false);
    }
  }, [viewMode, snapshotSizingMode]);

  useEffect(() => {
    if (viewMode !== 'basic') {
      snapshotImgRefs.current = {};
    }
  }, [viewMode]);

  useEffect(() => {
    if (!loupeActive) return undefined;
    const onMove = (e) => {
      setLoupePointer({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [loupeActive]);

  const snapshotOriginalLayout =
    viewMode === 'basic' && snapshotSizingMode === 'original';
  const fixedPageChrome = viewMode !== 'basic' || snapshotSizingMode === 'fit';
  const loupeFit = viewMode === 'basic' && snapshotSizingMode === 'fit';
  const enhancedPageFlipEnabled =
    viewMode !== 'enhanced' || Math.abs(clampedReaderFontScale - 1) < 0.001;
  const bookShadowSize = Math.round(
    680 * (viewMode === 'enhanced' ? clampedReaderFontScale : 1),
  );
  const fixedChromeInnerClassMain =
    viewMode === 'enhanced'
      ? 'absolute inset-0 flex justify-start items-start overflow-y-auto p-8 font-serif leading-relaxed text-gray-800 text-shadow whitespace-pre-line'
      : 'absolute inset-0 flex justify-start items-start overflow-y-auto p-8 text-md font-serif leading-relaxed text-gray-800 text-shadow whitespace-pre-line';
  const fixedChromeInnerClassStack =
    viewMode === 'enhanced'
      ? 'absolute inset-0 flex justify-start items-start p-8 font-serif leading-relaxed text-gray-800 text-shadow'
      : 'absolute inset-0 flex justify-start items-start p-8 text-md font-serif leading-relaxed text-gray-800 text-shadow';
  const enhancedTextStyle =
    viewMode === 'enhanced' && fixedPageChrome
      ? { fontSize: `${READER_FONT_BASE_PX * clampedReaderFontScale}px` }
      : undefined;

  const pageChrome = useMemo(
    () => getPageThemeChrome(selectedPageTheme),
    [selectedPageTheme],
  );

  const pageTextureStyle = useMemo(
    () => ({
      backgroundImage: `url(${selectedPageTheme?.theme_url || '/images/clear-white-plaster-texture-pattern.jpg'})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundColor: pageChrome.surfaceFallback,
    }),
    [pageChrome.surfaceFallback, selectedPageTheme?.theme_url],
  );

  let loupeSourceData = null;
  if (loupeActive && loupeFit) {
    const { x, y } = loupePointer;
    const indices = [currentPage * 2, currentPage * 2 + 1];
    for (let i = 0; i < indices.length; i += 1) {
      const idx = indices[i];
      const el = snapshotImgRefs.current[idx];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        const snap = snapshotsByIndex[idx];
        if (!snap?.snap_shot_url) continue;
        loupeSourceData = {
          url: snap.snap_shot_url,
          px: Math.max(0, Math.min(r.width, x - r.left)),
          py: Math.max(0, Math.min(r.height, y - r.top)),
          iw: r.width,
          ih: r.height,
        };
        break;
      }
    }
  }

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const loupeLensLeft =
    loupeActive && loupeFit
      ? Math.max(8, Math.min(vw - LOUPE_WIDTH - 8, loupePointer.x - LOUPE_WIDTH / 2))
      : 0;
  const loupeLensTop =
    loupeActive && loupeFit
      ? Math.max(8, Math.min(vh - LOUPE_HEIGHT - 8, loupePointer.y - LOUPE_HEIGHT / 2))
      : 0;

  const readerRootScroll =
    snapshotOriginalLayout
      ? 'overflow-x-auto overflow-y-auto max-h-[100dvh] md:max-h-screen'
      : 'overflow-auto';

  const backgroundThemesForDropdown = useMemo(
    () =>
      backgroundThemes.filter(
        (t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID,
      ),
    [backgroundThemes],
  );
  const pitchBlackThemeMeta = useMemo(
    () =>
      backgroundThemes.find(
        (t) => t.theme_meta_unique_id === PITCH_BLACK_THEME_ID,
      ),
    [backgroundThemes],
  );
  const isPitchBlackBackground =
    selectedBackgroundTheme?.theme_meta_unique_id === PITCH_BLACK_THEME_ID;
  const readerRootBackgroundStyle = isPitchBlackBackground
    ? {
        backgroundColor: '#000000',
        backgroundImage: 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.35)',
      }
    : {
        backgroundImage: `url(${selectedBackgroundTheme?.theme_url || '/images/wood.jpg'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.15)',
      };
  const listboxBackgroundTheme = useMemo(() => {
    if (!isPitchBlackBackground) return selectedBackgroundTheme;
    if (
      pitchBlackRestoreTheme?.theme_meta_unique_id &&
      pitchBlackRestoreTheme.theme_meta_unique_id !== PITCH_BLACK_THEME_ID
    ) {
      return pitchBlackRestoreTheme;
    }
    return (
      backgroundThemesForDropdown[0] ||
      backgroundThemes.find(
        (t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID,
      ) ||
      selectedBackgroundTheme
    );
  }, [
    isPitchBlackBackground,
    selectedBackgroundTheme,
    pitchBlackRestoreTheme,
    backgroundThemesForDropdown,
    backgroundThemes,
  ]);

  return (
    <div
      className={`relative flex justify-center bg-gray-200 h-auto md:h-screen ${readerRootScroll} pt-4`}
      style={readerRootBackgroundStyle}
    >

      {/* Initial Loader */}
      {initialLoading && (
        <div className="absolute inset-0 z-50 bg-white bg-opacity-80 flex items-center justify-center">
          <div className="animate-spin h-12 w-12 border-4 border-violet-800 border-t-transparent rounded-full"></div>
        </div>
      )}

      {showPremiumModal && previewTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-center">{previewTheme.theme_name}</h2>
            <img
              src={previewTheme.theme_url}
              alt={previewTheme.theme_name}
              className="w-full h-56 object-cover rounded mb-4"
            />
            {previewTheme.has_audio === 1 && (
              <audio controls className="w-full mb-4">
                <source src={previewTheme.audio_url} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            )}
            <p className="text-sm text-gray-600 text-center mb-6">
              This is a premium theme. Upgrade to access it.
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded"
                onClick={() => window.location.href = '/pricing'}
              >
                Go Premium
              </button>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setShowPremiumModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center space-y-4 w-[300px]">
              <h2 className="text-lg font-semibold">Delete Page?</h2>
              <p className="text-gray-600 text-sm">Are you sure you want to delete this page?</p>
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={() => {
                    deletePage(pageToDelete);
                    setShowDeleteModal(false);
                    setPageToDelete(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Yes, Delete
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setPageToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          )}

      {/* Settings Panel */}
        <div
          className={`fixed top-0 right-0 h-full w-72 bg-white shadow-lg p-6 z-40 flex flex-col gap-6 border-l border-gray-300 transform transition-transform duration-300 ${
            showSettings ? 'translate-x-0' : 'translate-x-full'
          }`}
          >
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Reader Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-red-500 text-xl"
              >
              &times;
            </button>
          </div>
          {/* Page Theme Dropdown */}
          <div>
            <label htmlFor="page-theme" className="block text-sm font-medium text-gray-600 mb-1">
              Page Theme
              </label>
                <select
                  id="page-theme"
                  value={selectedPageTheme?.theme_meta_unique_id || ''}
                  onChange={(e) => {
                    const theme = pageThemes.find(t => t.theme_meta_unique_id === e.target.value);
                    handlePageThemeChange(theme);
                  }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
              {pageThemes.map(theme => (
                <option key={theme.theme_meta_unique_id} value={theme.theme_meta_unique_id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>
          </div>
          {viewMode === 'enhanced' && (
            <div>
              <label
                htmlFor="reader-font-scale"
                className="block text-sm font-medium text-gray-600 mb-1"
              >
                Text size
              </label>
              <input
                id="reader-font-scale"
                type="range"
                min={READER_FONT_SCALE_MIN}
                max={READER_FONT_SCALE_MAX}
                step={0.05}
                value={readerFontScale}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v)) return;
                  setReaderFontScale(
                    Math.min(
                      READER_FONT_SCALE_MAX,
                      Math.max(READER_FONT_SCALE_MIN, v),
                    ),
                  );
                }}
                className="w-full"
              />
              <div className="mt-1 flex justify-between items-center text-xs text-gray-500">
                <span>Smaller</span>
                <button
                  type="button"
                  className="text-violet-900 hover:text-violet-950 underline"
                  onClick={() => setReaderFontScale(1)}
                >
                  Reset
                </button>
                <span>Larger</span>
              </div>
            </div>
          )}
          {/* Background Theme Dropdown */}
          <div>
            <label htmlFor="background-theme" className="block text-sm font-medium text-gray-600 mb-1">
              Background Theme
            </label>
              <ThemeDropdownWithPreview
                themes={backgroundThemesForDropdown}
                selectedTheme={listboxBackgroundTheme}
                setSelectedTheme={(theme) => {
                  handleBackgroundThemeChange(theme);
                }}
                onLockedThemeClick={(theme) => {
                  setPreviewTheme(theme);
                  setShowPremiumModal(true);
                  posthog.capture('click_locked_theme_preview', {
                    theme_name: theme.theme_name,
                    theme_id: theme.theme_meta_unique_id
                  });
                }}
              />
              {/*<select
                id="background-theme"
                value={selectedBackgroundTheme?.theme_meta_unique_id || ''}
                onChange={(e) => {
                  const theme = backgroundThemes.find(t => t.theme_meta_unique_id === e.target.value);
                  handleBackgroundThemeChange(theme);
                }}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
              {backgroundThemes.map(theme => (
                <option key={theme.theme_meta_unique_id} value={theme.theme_meta_unique_id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>*/}
          </div>
          {/* Audio Control */}
          {selectedBackgroundTheme?.has_audio === 1 && selectedBackgroundTheme.audio_url && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Audio</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={audioVolume}
                  onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                  className="w-full"
                />
                <button
                  onClick={() => setIsMuted(prev => !prev)}
                  className="mt-2 text-sm text-violet-900 underline"
                >
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
            </div>
          )}
          {loadingTheme && (
            <div className="text-sm text-gray-700 flex items-center gap-2">
              <span>Loading <strong>{loadingTheme.theme_name}</strong>...</span>
              <div className="w-4 h-4 border-2 border-violet-800 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

      {loadingSnapshots && (
        <div className="absolute top-4 right-4 z-50 bg-white bg-opacity-80 rounded-full p-2 shadow-lg">
          <div className="w-6 h-6 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setZoomedImage(null)}
        >
          <img
            src={zoomedImage}
            alt="Zoomed Visual"
            className="max-w-4xl max-h-[90vh] rounded shadow-lg object-contain"
          />
        </div>
      )}
      {/* Book Title Header 
      <header className="w-full flex justify-center py-4">
        <h1 className="text-3xl font-serif text-stone-100">
          My Book Title
        </h1>
      </header>*/}

      {/* Main Content: Book and Table Surface */}
      <div
        className={`relative w-full transition-opacity duration-500 ${
          fade ? 'opacity-100' : 'opacity-0'
        } flex items-start justify-start pt-5 ${
          snapshotOriginalLayout
            ? 'h-full max-h-[calc(100dvh-2rem)] overflow-auto md:max-h-[calc(100vh-2rem)] md:items-start md:justify-start'
            : viewMode === 'enhanced'
              ? 'relative w-full shrink-0 flex items-start justify-start overflow-x-auto overflow-y-auto py-2 pb-28'
              : 'h-full md:items-center md:justify-center md:pt-0'
        }`}
      >
        <div
          className="relative mx-auto"
          style={
            snapshotOriginalLayout
              ? { width: 'max-content', minHeight: 'min-content' }
              : { width: viewportLockWidth, height: fixedPanelPx }
          }
        >
        {/* Book */}
        <div
          className={`relative flex justify-center ${
            snapshotOriginalLayout ? 'items-stretch' : 'items-center'
          }`}
        >
           {/* Book Shadow */}
           <div
            className={`absolute rounded-full bg-black opacity-10 ${
              snapshotOriginalLayout ? 'hidden' : ''
            }`}
            style={
              snapshotOriginalLayout
                ? undefined
                : {
                    width: bookShadowSize,
                    height: bookShadowSize,
                    transform: 'translateY(10px)',
                    filter: 'blur(15px)',
                  }
            }
          ></div>
          {/* Left Page */}
          <div
            className={`rounded-l-md shadow-lg relative shrink-0 ${
              fixedPageChrome ? 'overflow-hidden' : 'h-auto w-auto min-w-0 overflow-visible'
            } ${
              fixedPageChrome &&
              enhancedPageFlipEnabled &&
              targetPage !== currentPage
                ? targetPage < currentPage
                  ? 'flip-prev'
                  : ''
                : ''
            }`}
          style={{
            transformOrigin: 'right center',
            ...(fixedPageChrome
              ? { width: fixedPanelPx, height: fixedPanelPx }
              : {}),
            ...pageTextureStyle,
            borderRightWidth: 4,
            borderRightStyle: 'solid',
            borderRightColor: pageChrome.accentColor,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)',
            zIndex: targetPage < currentPage ? 2 : 1, // Left page comes to top if flipping backwards
            }}>
            <div
              className={
                fixedPageChrome
                  ? fixedChromeInnerClassMain
                  : 'relative flex min-h-0 min-w-0 items-center justify-center p-2 text-md font-serif leading-relaxed text-gray-800 text-shadow'
              }
              style={enhancedTextStyle}
            >
            {renderPage(leftPage, currentPage * 2)}
            </div>
            {/* Page Number for Left Page */}
            {currentPage * 2 + 1 <= physicalTotalPages && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-md font-serif text-gray-800 leading-relaxed text-shadow">
                {currentPage * 2 + 1}
              </div>
            )}
            <div
              className="absolute top-0 left-0 w-3 h-full shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
            <div
              className="absolute bottom-0 left-0 w-full h-1 shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
          </div>

          {/* stack page left */}
          <div
            className={`absolute left-0 rounded-r-md shadow-md ${
              fixedPageChrome ? '' : 'h-auto w-auto min-w-0'
            }`}
          style={{
            zIndex: 0, // Further below the flipping page
            ...(fixedPageChrome
              ? { width: fixedPanelPx, height: fixedPanelPx }
              : {}),
            ...pageTextureStyle,
            borderRightWidth: 4,
            borderRightStyle: 'solid',
            borderRightColor: pageChrome.accentColor,
            }}
          >
            <div
              className={
                fixedPageChrome
                  ? fixedChromeInnerClassStack
                  : 'relative flex min-h-0 min-w-0 items-center justify-center p-2 text-md font-serif leading-relaxed text-gray-800 text-shadow'
              }
              style={enhancedTextStyle}
            >
            {renderPage(previousLeftPage, (currentPage - 1) * 2)}
            </div>
            <div
              className="absolute top-0 left-0 w-3 h-full shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
            <div
              className="absolute bottom-0 left-0 w-full h-1 shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
          </div>
          
          {/* Book Binding */}
          <div
            className={`w-[10px] shrink-0 ${
              fixedPageChrome ? '' : 'self-stretch'
            }`}
            style={{
              ...(fixedPageChrome ? { height: fixedPanelPx } : {}),
              backgroundColor: pageChrome.bindingColor,
            }}
          />

          {/* stack page right */}
          <div
            className={`absolute right-0 rounded-r-md shadow-md ${
              fixedPageChrome ? '' : 'h-auto w-auto min-w-0'
            }`}
          style={{
            zIndex: 0, // Further below the flipping page
            ...(fixedPageChrome
              ? { width: fixedPanelPx, height: fixedPanelPx }
              : {}),
            ...pageTextureStyle,
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            borderLeftColor: pageChrome.accentColor,
            }}
          >
            <div
              className={
                fixedPageChrome
                  ? fixedChromeInnerClassStack
                  : 'relative flex min-h-0 min-w-0 items-center justify-center p-2 text-md font-serif leading-relaxed text-gray-800 text-shadow'
              }
              style={enhancedTextStyle}
            >
            {renderPage(nextRightPage, (currentPage + 1) * 2 + 1)}
            </div>
            <div
              className="absolute top-0 right-0 w-3 h-full shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
            <div
              className="absolute bottom-0 right-0 w-full h-1 shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
          </div>

          {/* Right Page */}
          <div
            className={`rounded-r-md shadow-lg relative shrink-0 ${
              fixedPageChrome ? 'overflow-hidden' : 'h-auto w-auto min-w-0 overflow-visible'
            } ${
              fixedPageChrome &&
              enhancedPageFlipEnabled &&
              targetPage !== currentPage
                ? targetPage > currentPage
                  ? 'flip-next'
                  : ''
                : ''
            }`}
          style={{
            transformOrigin: 'left center',
            ...(fixedPageChrome
              ? { width: fixedPanelPx, height: fixedPanelPx }
              : {}),
            ...pageTextureStyle,
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            borderLeftColor: pageChrome.accentColor,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)',
            zIndex: targetPage > currentPage ? 3 : 1, // Right page comes to top if flipping forwards
            }}>
            <div
              className={
                fixedPageChrome
                  ? fixedChromeInnerClassMain
                  : 'relative flex min-h-0 min-w-0 items-center justify-center p-2 text-md font-serif leading-relaxed text-gray-800 text-shadow'
              }
              style={enhancedTextStyle}
            >
            {renderPage(rightPage, currentPage * 2 + 1)}
            </div>
            {currentPage * 2 + 2 <= physicalTotalPages && (
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-md font-serif text-gray-800 leading-relaxed text-shadow">
                {currentPage * 2 + 2}
              </div>
            )}
            <div
              className="absolute top-0 right-0 w-3 h-full shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
            <div
              className="absolute bottom-0 right-0 w-full h-1 shadow-inner"
              style={{ backgroundColor: pageChrome.accentColor }}
            />
          </div>
          {readingLampOn && (
            <div
              className="pointer-events-none absolute inset-0 z-[15] min-h-full min-w-full rounded-sm"
              style={{
                /* Full-spread dim warm wash; slightly brighter toward top (bulb) */
                background:
                  'linear-gradient(to bottom, rgba(255, 236, 218, 0.35) 0%, rgba(255, 234, 212, 0.025) 50%, rgba(255, 232, 208, 0.028) 100%), radial-gradient(ellipse 320% 300% at 50% -25%, rgba(255, 218, 175, 0.065) 0%, rgba(255, 228, 200, 0.042) 42%, rgba(255, 233, 210, 0.03) 72%, rgba(255, 235, 215, 0.022) 100%)',
                boxShadow: 'inset 0 0 200px 80px rgba(255, 228, 200, 0.022)',
              }}
              aria-hidden
            />
          )}
        </div>
        {/* Navigation Buttons */}
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center space-x-3 bottom-4 md:bottom-3">
            <button
              type="button"
              onClick={previousPage}
              disabled={jumpLoading || currentPage <= 0}
              className="w-16 h-16 bg-transparent border-2 border-violet-800 text-violet-800 rounded-full flex justify-center items-center shadow-md text-xl hover:bg-violet-800 hover:text-white transition-all disabled:cursor-not-allowed disabled:opacity-40"
            >
              &lt;
            </button>
            {physicalTotalPages > 0 ? (
              <div className="flex items-center gap-1 rounded-full border-2 border-violet-800 bg-white/90 px-2.5 py-1.5 text-violet-900 shadow-md">
                <label className="sr-only" htmlFor="reader-jump-page">
                  Jump to page
                </label>
                <select
                  id="reader-jump-page"
                  disabled={jumpLoading}
                  value={Math.min(displayedPhysicalPage, physicalTotalPages)}
                  onChange={(e) => {
                    void jumpToPhysicalPage(e.target.value);
                  }}
                  className="max-w-[4.5rem] cursor-pointer bg-transparent py-0.5 pl-1 pr-0 text-center text-sm font-medium outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Jump to page"
                >
                  {jumpPageOptions.map((page) => (
                    <option key={page} value={page}>
                      {page}
                    </option>
                  ))}
                </select>
                <span className="pr-1 text-sm font-medium text-violet-800/80">
                  / {physicalTotalPages}
                </span>
              </div>
            ) : null}
            <button
              type="button"
              onClick={nextPage}
              disabled={jumpLoading}
              className="w-16 h-16 bg-transparent border-2 border-violet-800 text-violet-800 rounded-full flex justify-center items-center shadow-md text-xl hover:bg-violet-800 hover:text-white transition-all disabled:cursor-not-allowed disabled:opacity-40"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      {!initialLoading && (
        <div className="absolute left-1/2 top-1 z-[25] flex -translate-x-1/2 items-center gap-2">
          {pitchBlackThemeMeta && (
            <button
              type="button"
              onClick={togglePitchBlackBackground}
              aria-pressed={isPitchBlackBackground}
              title={
                isPitchBlackBackground
                  ? 'Show background theme'
                  : 'Pitch black background'
              }
              aria-label={
                isPitchBlackBackground
                  ? 'Show background theme'
                  : 'Pitch black background'
              }
              className={`rounded-full border p-2 shadow-sm backdrop-blur-sm transition-colors ${
                isPitchBlackBackground
                  ? 'border-slate-600/90 bg-slate-800/95 text-amber-200'
                  : 'border-gray-300/90 bg-white/90 text-gray-800 hover:bg-white'
              }`}
            >
              {isPitchBlackBackground ? (
                <SunIcon className="h-3 w-3" aria-hidden />
              ) : (
                <MoonIcon className="h-3 w-3" aria-hidden />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setReadingLampOn((prev) => {
                const next = !prev;
                posthog.capture('reading_lamp_toggle', { book_id, on: next });
                return next;
              });
            }}
            aria-pressed={readingLampOn}
            title={readingLampOn ? 'Turn off reading lamp' : 'Turn on reading lamp'}
            aria-label={
              readingLampOn ? 'Turn off reading lamp' : 'Turn on reading lamp'
            }
            className={`rounded-full border p-2 shadow-sm backdrop-blur-sm transition-colors ${
              readingLampOn
                ? 'border-amber-400/90 bg-amber-50/95 text-amber-900'
                : 'border-gray-300/90 bg-white/90 text-gray-700 hover:bg-white'
            }`}
          >
            <LightBulbIcon className="h-3 w-3" aria-hidden />
          </button>
        </div>
      )}

      {/* Enhanced → basic link */}
      {viewMode === 'enhanced' && !snapshotsOnly && (
        <div className="absolute top-1 right-16 z-20">
          <button
            onClick={switchToBasicView}
            className="text-sm text-violet-900 underline hover:text-violet-950"
          >
            Switch to Basic View
          </button>
        </div>
      )}
      {/* Basic: fit vs original size + optional back to enhanced */}
      {viewMode === 'basic' && (
        <div className="absolute top-1 right-16 z-20 flex flex-row-reverse flex-wrap items-center justify-end gap-3">
          {snapshotSizingMode === 'fit' && (
            <button
              type="button"
              onClick={(e) => {
                setLoupeActive((prev) => {
                  const next = !prev;
                  if (next) {
                    setLoupePointer({ x: e.clientX, y: e.clientY });
                  }
                  posthog.capture('snapshot_loupe_toggle', { book_id, active: next });
                  return next;
                });
              }}
              className={`rounded-md border p-1.5 shadow hover:bg-white ${
                loupeActive
                  ? 'border-violet-900 bg-violet-50 text-violet-950'
                  : 'border-gray-400 bg-white/90 text-gray-700'
              }`}
              title={
                loupeActive
                  ? 'Turn off magnifying lens'
                  : 'Magnifying lens (follows pointer, 1.5×)'
              }
              aria-label={
                loupeActive ? 'Turn off magnifying lens' : 'Magnifying lens'
              }
            >
              <MagnifyingGlassIcon className="h-4 w-4" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              const next = snapshotSizingMode === 'fit' ? 'original' : 'fit';
              setSnapshotSizingMode(next);
              posthog.capture('snapshot_sizing_toggle', { book_id, mode: next });
            }}
            className="rounded-md border border-gray-400 bg-white/90 p-1.5 text-gray-700 shadow hover:bg-white"
            title={
              snapshotSizingMode === 'fit'
                ? 'Show original size (may scroll)'
                : 'Fit pages to screen'
            }
            aria-label={
              snapshotSizingMode === 'fit'
                ? 'Show original size'
                : 'Fit pages to screen'
            }
          >
            {snapshotSizingMode === 'fit' ? (
              <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden />
            ) : (
              <ArrowsPointingInIcon className="h-4 w-4" aria-hidden />
            )}
          </button>
          {!snapshotsOnly && (
            <button
              type="button"
              onClick={backToEnhancedView}
              className="text-sm text-violet-900 underline hover:text-violet-950"
            >
              Back to Enhanced View
            </button>
          )}
        </div>
      )}
      {/* Gear Icon */}
      <div className="absolute top-0 right-0 z-30">
      <button
          onClick={() => {
            setShowSettings(true);
            posthog.capture('toggle_settings_panel', {
              book_id,
              opened: true
            });
          }}
          className="p-3 text-gray-700 hover:text-violet-900 text-3xl"
          aria-label="Settings"
        >
          ⚙️
        </button>
      </div>
      {selectedBackgroundTheme && (
        <audio
          ref={audioRef}
          className="pointer-events-none fixed left-0 top-0 h-px w-px opacity-0"
          aria-hidden
          src={selectedBackgroundTheme.audio_url}
          loop
          autoPlay
          muted={isMuted}
          volume={audioVolume}
        />
      )}

      {loupeActive && loupeFit && (
        <div
          className="pointer-events-none fixed z-[45] overflow-hidden rounded-md border-2 border-violet-800/60 bg-white/20 shadow-xl ring-1 ring-black/10 backdrop-blur-[0.5px]"
          style={{
            width: LOUPE_WIDTH,
            height: LOUPE_HEIGHT,
            left: loupeLensLeft,
            top: loupeLensTop,
          }}
        >
          {loupeSourceData ? (
            <img
              src={loupeSourceData.url}
              alt=""
              className="absolute max-w-none select-none"
              draggable={false}
              style={{
                width: loupeSourceData.iw * LOUPE_ZOOM,
                height: loupeSourceData.ih * LOUPE_ZOOM,
                objectFit: 'fill',
                left: LOUPE_WIDTH / 2 - loupeSourceData.px * LOUPE_ZOOM,
                top: LOUPE_HEIGHT / 2 - loupeSourceData.py * LOUPE_ZOOM,
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-white/40 text-xs text-gray-600">
              Move over a page
            </div>
          )}
        </div>
      )}
      {promoElement}
    </div>
  );
}

export default BasicTheme;